import { useCallback, useRef } from "react";
import { MODALITY } from "../classes/enums/MedicalImagesEnum";
import { postPatientMorphologyPicture } from "../services/ImageService";
import { postPatientMedicalImages } from "../services/InstancesService";
import { formatMorphologyFilename } from "./useFileUploadHooks";

// Import stages for tracking progress
export const ImportStage = {
  UPLOADING: "uploading",
  PROCESSING: "processing",
  COMPLETE: "complete",
};

export const useImageImport = options => {
  const {
    patientId,
    onSave,
    setIsImporting,
    setImportProgress,
    onStageChange,
    setState,
    successState,
    errorState,
    onComplete,
    onProcessingComplete,
  } = options;

  // Track pending processing operations
  const pendingProcessingRef = useRef(0);
  const processingErrorsRef = useRef([]);

  const handleImport = useCallback(
    async processedGroups => {
      setIsImporting?.(true);
      setImportProgress?.(0);
      onStageChange?.(ImportStage.UPLOADING);

      // Reset tracking
      pendingProcessingRef.current = 0;
      processingErrorsRef.current = [];

      try {
        const morphologicalGroups = processedGroups.filter(
          group => group.modality === MODALITY.MO
        );
        const otherGroups = processedGroups.filter(
          group => group.modality !== MODALITY.MO
        );

        const morphologyFilesCount = morphologicalGroups.reduce(
          (total, group) => total + group.files.length,
          0
        );
        const otherFilesCount = otherGroups.reduce(
          (total, group) => total + group.files.length,
          0
        );
        const totalFiles = morphologyFilesCount + otherFilesCount;
        let processedFiles = 0;

        // Count how many groups will need processing
        pendingProcessingRef.current = otherGroups.length;

        for (const group of morphologicalGroups) {
          for (const fileItem of group.files) {
            setImportProgress?.(
              Math.round((processedFiles / totalFiles) * 100)
            );

            const formattedFilename = formatMorphologyFilename(
              fileItem.name,
              group.modality,
              group.orientation,
              group.creationDate
            );

            await postPatientMorphologyPicture(
              patientId,
              fileItem,
              formattedFilename
            );

            processedFiles++;
            setImportProgress?.(
              Math.round((processedFiles / totalFiles) * 100)
            );
          }
        }

        // Track if we've transitioned to processing stage
        let hasTransitionedToProcessing = false;

        for (const group of otherGroups) {
          const files = group.files;
          const metadata = {
            orientation: group.orientation,
            originalOrientation: group.originalOrientation,
            modality: group.modality,
            dxPlane: group.dxPlane || null,
            vertebra: group.vertebra,
            hasT13: group.hasT13,
            hasS1Lumbarized: group.hasS1Lumbarized,
            hasL5Sacralized: group.hasL5Sacralized,
            creationDate: group.creationDate || group.date,
          };

          await postPatientMedicalImages(patientId, files, metadata, {
            onUploadComplete: () => {
              // Transition to processing stage after first upload completes
              if (!hasTransitionedToProcessing) {
                hasTransitionedToProcessing = true;
                onStageChange?.(ImportStage.PROCESSING);
              }
            },
            onProcessingComplete: (success, result) => {
              pendingProcessingRef.current--;

              if (!success) {
                processingErrorsRef.current.push(result);
              }


              // All processing complete
              if (pendingProcessingRef.current === 0) {
                const allSuccess = processingErrorsRef.current.length === 0;
                onStageChange?.(ImportStage.COMPLETE);
                onProcessingComplete?.(allSuccess);
              }
            },
          });

          processedFiles += files.length;
          setImportProgress?.(Math.round((processedFiles / totalFiles) * 100));
        }

        // If no groups needed processing, mark as complete
        if (otherGroups.length === 0) {
          onStageChange?.(ImportStage.COMPLETE);
          onProcessingComplete?.(true);
        }

        if (onSave) {
          onSave(processedGroups);
        }

        if (setState && successState) {
          setState(successState);
        }

        // onComplete is for upload completion (UI can proceed)
        if (onComplete) {
          onComplete(true);
        }
      } catch (error) {
        if (setState && errorState) {
          setState(errorState);
        }

        if (onComplete) {
          onComplete(false);
        }

        // Also notify processing complete with error
        onProcessingComplete?.(false);
      } finally {
        setIsImporting?.(false);
      }
    },
    [
      patientId,
      onSave,
      setIsImporting,
      setImportProgress,
      onStageChange,
      setState,
      successState,
      errorState,
      onComplete,
      onProcessingComplete,
    ]
  );

  return { handleImport };
};
